import styles from "./Loans.module.scss";
import React from "react";
import { HeaderMenu } from "../../../common/HeaderMenu";
import { MODE_EDIT } from "../../../codes";
import useLoans from "./useLoans";
import useFormat from "../../../util/useFormat";
import clsx from "clsx";
import { VariableSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { LoanEditor } from "../loan-editor/LoanEditor";
import useProject from "../../../util/useProject";
import { LoansFab } from "./LoansFab";
import { Content, ContentFixed, ContentScroll } from "../../../common/Content";

const Loans = () => {
  const { formatDate, formatAmount, formatNumber } = useFormat();
  const { runWithExpiration } = useProject();

  const {
    t,
    allLoans,
    showLoanEditor,
    addInstallment,
    removeInstallment,
    pendingInstallmentLoanId
  } = useLoans();

  const loans = allLoans.toSorted((a, b) => {
    const totalAmountA = a.premium * a.duration;
    const reimbursedA = a.installments.reduce((a, b) => a + b.amount, 0);

    const totalAmountB = b.premium * b.duration;
    const reimbursedB = b.installments.reduce((a, b) => a + b.amount, 0);

    return (reimbursedA / totalAmountA) - (reimbursedB / totalAmountB);
  })

  const Row = ({ index, style }) => {
    const loan = loans[index];

    const totalAmount = loan.premium * loan.duration;
    const reimbursed = loan.installments.reduce((a, b) => a + b.amount, 0);
    const lastInstallmentDate = loan.installments.reduce(
      (last, installment) => !last || installment.valueDate > last ? installment.valueDate : last,
      null
    );

    return <div style={style} className={styles.loan}>
      <div className={clsx(styles.details, "hover-selectable")}
           onClick={runWithExpiration(() => showLoanEditor(MODE_EDIT, loan))}>
        <div className={styles.name}>
          <div className={styles.namePercentage}>
            <span>{loan.name}</span>
            <span>({formatNumber(reimbursed / totalAmount * 100)}%)</span>
          </div>
          <div>
            <div className={styles.progressBar}>
              <div className={styles.progressBarProgress}
                   style={{ width: (reimbursed / totalAmount * 100) + "%" }}></div>
            </div>
          </div>
        </div>
        <div className={styles.period}>
          <div className={styles.start_date}>{formatDate(loan.startDate)}</div>
          <div className={styles.periodArrow}><span className="material-icons">arrow_right_alt</span></div>
          <div className={styles.end_date}>{formatDate(loan.endDate)}</div>
        </div>
        <div className={styles.duration}>{loan.duration} {t("mois")}</div>
        <div className={styles.rate}>{formatNumber(loan.rate, "#T##0D000")}</div>
        <div className={styles.amounts}>
          <div className={styles.premium}>
            {formatAmount(loan.premium)}<span className={styles.perMonth}> / {t("mois")}</span>
          </div>
          <div className={clsx(styles.amount, styles.borrowedAmount)}>{formatAmount(loan.amount)}</div>
          <div className={clsx(styles.amount, styles.totalAmount)}>{formatAmount(totalAmount)}</div>
          <div className={clsx(styles.amount, styles.reimbursed)}>{formatAmount(reimbursed)}</div>
          <div className={styles.reimbursedOf}>/</div>
        </div>
        <div className={styles.last_installment_date}>
          <span className={clsx("material-icons", styles.lastInstallmentIcon)}>event_available</span>
          {lastInstallmentDate ? formatDate(lastInstallmentDate) : "-"}
        </div>
      </div>
      <div className={styles.installmentActions}>
          <button type="button"
                className={clsx(styles.installmentAction, "hover-selectable")}
                title={t("Ajouter une échéance")}
                disabled={pendingInstallmentLoanId === loan.id}
                onClick={runWithExpiration(() => addInstallment(loan))}>
          <span className="material-icons">add_circle_outline</span>
        </button>
        <button type="button"
                className={clsx(styles.installmentAction, styles.removeInstallment, "hover-selectable")}
                title={t("Supprimer la dernière échéance")}
                disabled={pendingInstallmentLoanId === loan.id || loan.installments.length === 0}
                onClick={runWithExpiration(() => removeInstallment(loan))}>
          <span className="material-icons">remove_circle_outline</span>
        </button>
      </div>
    </div>;
  };

  const allLoansTotalAmount = loans.reduce((a, loan) => a + (loan.premium * loan.duration), 0);
  const allLoansReimbursed = loans.reduce((a, loan) => a + loan.installments.reduce((a, b) => a + b.amount, 0), 0);

  return (
    <Content>
      <ContentFixed>
        <HeaderMenu title={t("Emprunts")} />

        <LoanEditor />

        <LoansFab />

        <div className={clsx(styles.loan, styles.header)}>
          <div className={styles.details}>
            <div className={styles.name}>{t("Nom")}</div>
            <div className={styles.period}>
              <div className={styles.start_date}>{t("Date début")}</div>
              <div className={styles.end_date}>{t("Date fin")}</div>
            </div>
            <div className={styles.duration}>{t("Durée")}</div>
            <div className={styles.rate}>{t("Taux")}</div>
            <div className={styles.premium}>{t("Prime")}</div>
            <div className={clsx(styles.amount, styles.borrowedAmount)}>{t("Montant")}</div>
            <div className={clsx(styles.amount, styles.totalAmount)}>{t("Montant total")}</div>
            <div className={clsx(styles.amount, styles.reimbursed)}>{t("Remboursé")}</div>
            <div className={styles.last_installment_date}>{t("Dernier remboursement")}</div>
          </div>
          <div className={styles.installmentActionsPlaceholder}></div>
        </div>

        <div className={clsx(styles.loan, styles.total)}>
          <div className={clsx(styles.details, "hover")}>
            <div className={styles.name}>
              <div className={styles.namePercentage}>
                <span>Total</span>
                <span>({formatNumber(allLoansReimbursed / allLoansTotalAmount * 100)}%)</span>
              </div>
              <div>
                <div className={styles.progressBar}>
                  <div className={styles.progressBarProgress}
                       style={{ width: (allLoansReimbursed / allLoansTotalAmount * 100) + "%" }}></div>
                </div>
              </div>
            </div>
            <div className={styles.period}>
              <div className={styles.start_date}>-</div>
              <div className={styles.end_date}>-</div>
            </div>
            <div className={styles.duration}>-</div>
            <div className={styles.rate}>-</div>
            <div className={styles.amounts}>
              <div className={styles.premium}>
                {formatAmount(loans.reduce((a, loan) => a + loan.premium, 0))}
                <span className={styles.perMonth}> / {t("mois")}</span>
              </div>
              <div className={clsx(styles.amount, styles.borrowedAmount)}>
                {formatAmount(loans.reduce((a, loan) => a + loan.amount, 0))}
              </div>
              <div className={clsx(styles.amount, styles.totalAmount)}>{formatAmount(allLoansTotalAmount)}</div>
              <div className={clsx(styles.amount, styles.reimbursed)}>{formatAmount(allLoansReimbursed)}</div>
              <div className={styles.reimbursedOf}>/</div>
            </div>
            <div className={styles.last_installment_date}>-</div>
          </div>
          <div className={styles.installmentActionsPlaceholder}></div>
        </div>
      </ContentFixed>

      <ContentScroll whiteBackground>
        <AutoSizer>
          {({ height, width }) => (
            <VariableSizeList
              className="custom-scrollbar autosizer-scrollbar"
              style={{ overflowY: "scroll" }}
              height={height - 0.5}
              itemCount={loans.length}
              itemSize={() => width < 1080 ? 96 : 48}
              estimatedItemSize={width < 1080 ? 96 : 48}
              itemKey={index => loans[index].id}
              width={width}
              overscanCount={10}
              useIsScrolling
            >
              {Row}
            </VariableSizeList>
          )}
        </AutoSizer>
      </ContentScroll>
    </Content>
  );
};

export default Loans;
